import React, { useEffect, useState } from 'react';
import { RefreshCw, Trash2, Receipt } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import AdminReassignDriverButton from './AdminReassignDriverButton';
import ConfirmPaymentButton from '../components/ConfirmPaymentButton';
import ETransferProofView from '../components/ETransferProofView';
import { toast } from 'sonner';

const STATUSES = ['all', 'placed', 'confirmed', 'preparing', 'on_the_way', 'delivered', 'cancelled'];
const STATUS_COLORS = {
  placed: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-blue-50 text-blue-700',
  preparing: 'bg-amber-50 text-amber-700',
  on_the_way: 'bg-emerald-50 text-emerald-700',
  delivered: 'bg-slate-100 text-slate-500',
  cancelled: 'bg-rose-50 text-rose-700',
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [proofModal, setProofModal] = useState(null); // { orderId, submission, expected, currency }

  const openProof = async (o) => {
    try {
      const resp = await adminApi.getOrderETransfer(o.id, 'platform');
      const sub = (resp?.submissions || [])[0] || null;
      const expected = Number(o.platform_invoice_amount ?? o.platform_amount ?? ((Number(o.delivery_fee ?? o.deliveryFee) || 0) + (Number(o.service_fee ?? o.serviceFee) || 0)));
      setProofModal({ orderId: o.id, submission: sub, expected, currency: o.currency || 'CAD' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load e-transfer proof');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.orders({ status_f: filter === 'all' ? undefined : filter });
      setOrders(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const changeStatus = async (id, status) => {
    try {
      await adminApi.updateOrderStatus(id, status);
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(`Order ${id} → ${status.replace(/_/g, ' ')}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete order ${id}?\n\nThis is irreversible and also removes its payment/transaction records.`)) return;
    try {
      await adminApi.deleteOrder(id);
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success(`Order ${id} deleted`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const confirmPlatformPayment = async (id, method, reference) => {
    const resp = await adminApi.confirmPlatformPayment(id, method, reference);
    setOrders((prev) => prev.map((o) => (o.id === id ? {
      ...o,
      platform_invoice_status: resp.platform_invoice_status,
      goods_invoice_status: resp.goods_invoice_status,
      assignment_status: resp.assignment_status,
    } : o)));
    return resp;
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Orders</h1>
          <p className="text-slate-500 mt-1">All orders across the platform — delivery-related fees only (PAY FOR ME orders show the full amount).</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s.replace(/_/g, ' ')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-orders-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-3">Order</th>
                <th className="text-left font-medium px-4 py-3">Vendor</th>
                <th className="text-right font-medium px-4 py-3">Tax</th>
                <th className="text-right font-medium px-4 py-3">Delivery</th>
                <th className="text-right font-medium px-4 py-3">Service</th>
                <th className="text-right font-medium px-4 py-3">Delivery fees</th>
                <th className="text-left font-medium px-4 py-3">Driver</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-right font-medium px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.length === 0 ? (
                <tr><td colSpan="9" className="text-center text-slate-500 py-10">No orders found.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`admin-order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 whitespace-nowrap">
                    <div>{o.id}</div>
                    <div className="text-[10px] text-slate-400">{o.placedAt ? new Date(o.placedAt).toLocaleString() : '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{o.vendor_name || o.vendorName}</td>
                  <td className="px-4 py-3 text-right text-slate-700" data-testid={`admin-order-tax-${o.id}`}>{o.pay_for_me ? money(o.tax_amount) : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-right text-slate-700" data-testid={`admin-order-delivery-${o.id}`}>{money(o.delivery_fee ?? o.deliveryFee)}</td>
                  <td className="px-4 py-3 text-right text-slate-700" data-testid={`admin-order-service-${o.id}`}>{money(o.service_fee ?? o.serviceFee)}</td>
                  {/* Non-PFM: only delivery-related fees (delivery + service). PAY FOR ME: full total — ShipitNow collects everything. */}
                  <td className="px-4 py-3 text-right font-semibold text-slate-900" data-testid={`admin-order-total-${o.id}`}>
                    {o.pay_for_me ? (
                      <div className="flex flex-col items-end">
                        <span>{money(o.total)}</span>
                        <span className="text-[9px] font-semibold text-indigo-600 uppercase tracking-wide" data-testid={`admin-pfm-tag-${o.id}`}>Pay for me · full</span>
                      </div>
                    ) : (
                      money((Number(o.delivery_fee ?? o.deliveryFee) || 0) + (Number(o.service_fee ?? o.serviceFee) || 0))
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[140px] truncate">
                    {o.driver?.name || o.driver_name || <span className="text-slate-400 italic">Unassigned</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <Badge className={`${STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-700'} border-0 hover:opacity-100 capitalize w-fit`}>{(o.status || 'placed').replace(/_/g, ' ')}</Badge>
                      {o.assignment_status === 'awaiting_payment' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium w-fit" data-testid={`admin-invoice-pending-${o.id}`}>
                          Awaiting payment
                        </span>
                      )}
                      <div className="flex gap-1 text-[10px]">
                        <span className={`px-1.5 py-0.5 rounded ${o.goods_invoice_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : o.goods_invoice_status === 'not_required' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`} data-testid={`admin-goods-invoice-${o.id}`}>
                          Goods: {o.goods_invoice_status === 'not_required' ? 'n/a' : (o.goods_invoice_status || 'pending')}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded ${o.platform_invoice_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid={`admin-platform-invoice-${o.id}`}>
                          ShipitNow: {o.platform_invoice_status || 'pending'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                      {o.platform_invoice_status === 'pending' && (o.fulfill_pay_method === 'etransfer' || o.platform_invoice_method === 'etransfer') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openProof(o)}
                          data-testid={`admin-view-etransfer-proof-${o.id}`}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          <Receipt className="h-3.5 w-3.5 mr-1" /> View e-transfer
                        </Button>
                      )}
                      <ConfirmPaymentButton
                        kind="platform"
                        orderId={o.id}
                        amount={Number(o.platform_invoice_amount ?? o.platform_amount ?? ((Number(o.delivery_fee ?? o.deliveryFee)||0) + (Number(o.service_fee ?? o.serviceFee)||0)))}
                        currency={o.currency || 'CAD'}
                        status={o.platform_invoice_status || 'pending'}
                        onConfirm={({ method, reference }) => confirmPlatformPayment(o.id, method, reference)}
                        buttonSize="sm"
                      />
                      {!['delivered', 'cancelled', 'refunded', 'failed'].includes(o.status) && (
                        <AdminReassignDriverButton
                          orderId={o.id}
                          currentDriverId={o.driver_id}
                          currentDriverName={o.driver?.name || o.driver_name}
                          onReassigned={() => load()}
                        />
                      )}
                      <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                        <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUSES.filter((s) => s !== 'all').map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => remove(o.id)}
                        className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
                        data-testid={`admin-delete-order-${o.id}`}
                        title="Hard-delete this order and its payment records">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!proofModal} onOpenChange={(o) => !o && setProofModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-600" />
              E-transfer proof — {proofModal?.orderId}
            </DialogTitle>
          </DialogHeader>
          {proofModal && (
            <div className="space-y-3 pt-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Fulfillment invoice</div>
                <div className="text-2xl font-bold text-slate-900 mt-1">
                  {proofModal.currency} {Number(proofModal.expected).toFixed(2)}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">Delivery + service — paid to ShipitNow.</div>
              </div>
              <ETransferProofView
                submission={proofModal.submission}
                amountExpected={proofModal.expected}
                testidPrefix="admin-etransfer"
              />
              <p className="text-[11px] text-slate-500">
                Verify the amount and reference match your bank record, then approve on the E-Transfers page or use the &ldquo;Confirm ShipitNow payment&rdquo; button.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
